// @ts-nocheck
/**
 * Native Mapbox GL fleet track layer — the GL analogue of MultiTrackLayer. Flag-gated by MAP_GL_NATIVE.
 *
 * Fleet tracks are SOLID per-source color (maptype is ignored for color, matching the SVG behavior), so
 * this builds one base `line` layer (all sources' gap-split runs merged, keyed by sourceId) + one
 * selection-overlay `line` layer, via renderers/buildTrackFeatures. Highlight/dim is data-driven
 * (`line-opacity`/`line-width` from feature properties) so it needs no per-frame work.
 *
 * NOT handled here (unchanged division of labor): track click/drag → SelectionLayer; maneuver markers →
 * GlManeuverLayer. Both are mounted alongside this by MapContainer.
 */

import { createEffect, onCleanup, untrack } from 'solid-js';
import { buildTrackFeatures } from '../../renderers/buildTrackFeatures';
import { resolveTrackColor } from '../../renderers/boatMarkerStyle';
import { sourcesStore } from '../../../../../store/sourcesStore';
import {
  selectedRanges,
  selectedRange,
  cutEvents,
  isCut,
} from '../../../../../store/selectionStore';
import { selectedTime, timeWindow, isPlaying, isUnsetPlaybackTime } from '../../../../../store/playbackStore';
import { defaultChannelsStore } from '../../../../../store/defaultChannelsStore';
import { getInterpolatedPointAtTime } from '../../../../../utils/trackInterpolation';
import { isLongBrushSelection } from '../../../../../utils/fleetTimeWindow';
import {
  getExpandedViewportBounds,
  getCombinedSampleRate,
  sampleTrack,
} from '../../utils/lodUtils';
import { createTrackImportanceCache, trackSimplifyTolerance } from '../../utils/trackGeometry';

const GAP_THRESHOLD_MS = 10000;

export interface GlMapMultiTrackLayerProps {
  data: any[];
  map: any;
  maptype: string;
  samplingFrequency: number;
  selectedSourceIds: Set<number>;
  highlightedSourceIds?: Set<number>;
  effectivePlaybackTime?: Date | null;
}

let instanceCounter = 0;

export default function GlMapMultiTrackLayer(props: GlMapMultiTrackLayerProps) {
  const uid = `fleet-gl-track-${instanceCounter++}`;
  const baseSrc = `${uid}-base-src`;
  const baseLyr = `${uid}-base-lyr`;
  const selSrc = `${uid}-sel-src`;
  const selLyr = `${uid}-sel-lyr`;
  // Signature of last rendered state — dedups rebuilds (incl. the 'idle' safety net) to avoid render loops.
  let lastRenderSig = '';
  const importanceBySource = new Map<number, ReturnType<typeof createTrackImportanceCache>>();
  const importanceFor = (sourceId: number, track: readonly unknown[]) => {
    let cache = importanceBySource.get(sourceId);
    if (!cache) {
      cache = createTrackImportanceCache();
      importanceBySource.set(sourceId, cache);
    }
    return cache(track, getLat, getLng, 'fleet');
  };

  const sourceKey = (d: any) => d?.source_id ?? d?.Source_id ?? d?.sourceId ?? d?.sourceID;
  const { latName, lngName } = defaultChannelsStore;
  const getLat = (d: any): number | undefined => {
    const f = latName();
    const v = d?.[f] ?? d?.[f.toLowerCase()] ?? d?.[f.toUpperCase()] ?? d?.Lat ?? d?.lat;
    const n = Number(v);
    return v == null || isNaN(n) ? undefined : n;
  };
  const getLng = (d: any): number | undefined => {
    const f = lngName();
    const v = d?.[f] ?? d?.[f.toLowerCase()] ?? d?.[f.toUpperCase()] ?? d?.Lng ?? d?.lng;
    const n = Number(v);
    return v == null || isNaN(n) ? undefined : n;
  };
  const getTimestamp = (d: any): Date => {
    const t = d?.Datetime ?? d?.timestamp ?? d?.time ?? d?.datetime;
    if (!t) return new Date(0);
    if (t instanceof Date) return t;
    const parsed = new Date(t);
    return isNaN(parsed.getTime()) ? new Date(0) : parsed;
  };

  const styleReady = () => {
    const map = props.map;
    return !!map && typeof map.isStyleLoaded === 'function' && map.isStyleLoaded();
  };
  const emptyFc = () => ({ type: 'FeatureCollection', features: [] });

  const ensureLayers = (map: any) => {
    if (!map.getSource(baseSrc)) {
      map.addSource(baseSrc, { type: 'geojson', data: emptyFc() });
      map.addLayer({
        id: baseLyr,
        type: 'line',
        source: baseSrc,
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: {
          'line-color': ['get', 'color'],
          'line-width': ['get', 'width'],
          'line-opacity': ['get', 'opacity'],
        },
      });
    }
    if (!map.getSource(selSrc)) {
      map.addSource(selSrc, { type: 'geojson', data: emptyFc() });
      map.addLayer({
        id: selLyr,
        type: 'line',
        source: selSrc,
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: { 'line-color': ['get', 'color'], 'line-width': ['get', 'width'] },
      });
    }
  };
  const removeLayers = (map: any) => {
    try {
      for (const id of [baseLyr, selLyr]) if (map.getLayer(id)) map.removeLayer(id);
      for (const id of [baseSrc, selSrc]) if (map.getSource(id)) map.removeSource(id);
    } catch (_) {
      /* map torn down */
    }
  };

  // ── Group + filter per source (port of MultiTrackLayer.groupedData, sans logging) ──
  const computeGroups = () => {
    const raw = Array.isArray(props.data) ? props.data : [];
    const timeForFilter = props.effectivePlaybackTime ?? selectedTime();
    const tw = Number(timeWindow());
    const brush = selectedRange();
    const longBrushActive = isLongBrushSelection(brush);

    let selected = props.selectedSourceIds || new Set<number>();
    const valid = raw.filter((pt) => {
      const sid = sourceKey(pt);
      return sid != null && Number.isFinite(Number(sid));
    });
    if (selected.size === 0) {
      const inData = new Set<number>();
      for (const pt of valid) {
        const sid = Number(sourceKey(pt));
        if (Number.isFinite(sid)) inData.add(sid);
      }
      if (inData.size === 0) return [];
      selected = inData;
    }

    const brushRanges: Array<{ start_time: any; end_time: any }> = Array.isArray(brush) ? brush : [];
    const inBrush = (d: any) => {
      if (brushRanges.length === 0) return true;
      const t = getTimestamp(d).getTime();
      return brushRanges.some((r) => {
        const s = r.start_time instanceof Date ? r.start_time.getTime() : new Date(r.start_time).getTime();
        const e = r.end_time instanceof Date ? r.end_time.getTime() : new Date(r.end_time).getTime();
        return t >= s && t <= e;
      });
    };

    const sources = sourcesStore.sources();
    const out: any[] = [];
    for (const sid of selected) {
      const sourceId = Number(sid);
      if (!Number.isFinite(sourceId)) continue;
      let sourceData = valid.filter((pt) => Number(sourceKey(pt)) === sourceId);
      if (sourceData.length === 0) continue;
      const fullSourceData = [...sourceData];

      sourceData = sourceData.filter(inBrush);
      if (tw > 0 && !longBrushActive && timeForFilter && !isUnsetPlaybackTime(timeForFilter)) {
        const before = sourceData;
        const windowStart = new Date(timeForFilter.getTime() - tw * 60 * 1000);
        sourceData = sourceData.filter((d) => {
          const t = getTimestamp(d);
          return t >= windowStart && t <= timeForFilter;
        });
        const interp = getInterpolatedPointAtTime(before, timeForFilter, { getTimestamp, getLat, getLng });
        if (interp) sourceData = [...sourceData, interp];
      } else if (tw === 0 && sourceData.length < fullSourceData.length && brushRanges.length === 0) {
        sourceData = fullSourceData;
      }

      const validCoords = sourceData.filter((d) => {
        const lng = getLng(d);
        const lat = getLat(d);
        return lng !== undefined && lat !== undefined && Number.isFinite(lng) && Number.isFinite(lat);
      });
      const sorted = [...validCoords].sort((a, b) => getTimestamp(a).getTime() - getTimestamp(b).getTime());

      // timeWindow 0 + playing: clip to playhead + 1s so boat isn't ahead of its track.
      let trackData = sorted;
      const st = selectedTime();
      if (tw === 0 && st && !isUnsetPlaybackTime(st) && isPlaying()) {
        const cutoff = st.getTime() + 1000;
        trackData = sorted.filter((d) => getTimestamp(d).getTime() <= cutoff);
      }
      if (trackData.length < 2) continue;

      const info = sources.find((s) => Number(s.source_id) === sourceId);
      out.push({
        sourceId,
        sourceName: info?.source_name || String(sourceId),
        color: resolveTrackColor(info?.source_name, info?.color || '#1f77b4'),
        data: trackData,
        fullData: fullSourceData,
      });
    }
    return out;
  };

  const rangeMatchesSource = (range: any, group: any): boolean => {
    const rid = range.source_id ?? range.sourceId;
    if (rid != null && Number.isFinite(Number(rid))) return Number(rid) === group.sourceId;
    const rname = range.source_name ?? range.sourceName;
    if (rname && group.sourceName) return String(rname).toLowerCase() === String(group.sourceName).toLowerCase();
    return false;
  };

  const rebuild = () => {
    try {
      rebuildInner();
    } catch (err) {
      // Never let a transient error kill this effect's reactivity (would freeze stale fleet tracks).
       
      console.warn('[GlMapMultiTrackLayer] rebuild failed:', err);
    }
  };

  const rebuildInner = () => {
    const map = props.map;
    if (!map || !styleReady()) return;
    ensureLayers(map);

    // Dedup: skip when nothing that affects output changed. Required because rebuild also runs on map 'idle'
    // (so late-loading sources still get drawn) — without this, setData→repaint→idle→rebuild would loop.
    const raw = Array.isArray(props.data) ? props.data : [];
    const tf = props.effectivePlaybackTime ?? selectedTime();
    const sig = [
      raw.length ? `${raw.length}_${getTimestamp(raw[0]).getTime()}_${getTimestamp(raw[raw.length - 1]).getTime()}` : 'e',
      Array.from(props.selectedSourceIds || []).sort((a, b) => a - b).join(','),
      Array.from(props.highlightedSourceIds || []).sort((a, b) => a - b).join(','),
      tf instanceof Date ? tf.getTime() : 0,
      Number(timeWindow()),
      JSON.stringify(selectedRange()),
      JSON.stringify(selectedRanges()),
      isCut() ? JSON.stringify(cutEvents()) : '',
      Math.round(map.getZoom() * 4) / 4,
    ].join('|');
    if (sig === lastRenderSig) return;
    lastRenderSig = sig;

    const groups = computeGroups();
    if (groups.length === 0) {
      map.getSource(baseSrc)?.setData(emptyFc());
      map.getSource(selSrc)?.setData(emptyFc());
      return;
    }

    const currentZoom = props.map.getZoom();
    const viewportBounds = getExpandedViewportBounds(props.map);
    const highlighted = props.highlightedSourceIds ?? new Set<number>();
    const hasHighlight = highlighted.size > 0;
    const ranges = selectedRanges();
    const hasEventSelections = Array.isArray(ranges) && ranges.length > 0;
    const baseOpacity = hasEventSelections ? 0.5 : 1;
    const cutOn = isCut() && cutEvents().length > 1;
    const cut = cutOn ? { isCut: true, cutEvents: cutEvents() } : undefined;

    const baseFeatures: any[] = [];
    const selFeatures: any[] = [];

    for (const group of groups) {
      // LOD-sample, then drop the latest point (boat sits at the head of the track).
      const lod = getCombinedSampleRate(currentZoom, group.data, viewportBounds, getLat, getLng, 5000);
      let sampled = lod.sampleRate > 1 ? sampleTrack(group.data, lod.sampleRate, currentZoom) : group.data;
      if (sampled.length > 1) sampled = sampled.slice(0, -1);
      if (sampled.length < 2) continue;

      const isHi = hasHighlight && highlighted.has(group.sourceId);
      const width = isHi ? 3 : 1;
      const opacity = isHi ? 1 : hasHighlight ? 0.3 : baseOpacity;

      const simplify = {
        importance: importanceFor(group.sourceId, group.data),
        tolerance: trackSimplifyTolerance(currentZoom),
      };
      const built = buildTrackFeatures({
        data: sampled,
        getColor: () => group.color,
        getThickness: () => width,
        config: { maptype: 'DEFAULT', showGaps: true, zoomLevel: 16, gapThreshold: GAP_THRESHOLD_MS },
        getLat,
        getLng,
        getTimestamp,
        samplingFrequency: props.samplingFrequency,
        legBridgeMs: GAP_THRESHOLD_MS,
        cut,
        baseFeatureProps: { sourceId: group.sourceId, opacity },
        simplify,
      });
      baseFeatures.push(...built.base.features);

      // Event-selection overlays use the FULL-DAY track (not clipped), matched per source.
      if (hasEventSelections) {
        const matched = ranges.filter((r) => rangeMatchesSource(r, group));
        if (matched.length > 0) {
          const fullSorted = [...group.fullData]
            .filter((d) => getLng(d) !== undefined && getLat(d) !== undefined)
            .sort((a, b) => getTimestamp(a).getTime() - getTimestamp(b).getTime());
          const sel = buildTrackFeatures({
            data: fullSorted,
            getColor: () => group.color,
            getThickness: () => 3,
            config: { maptype: 'DEFAULT', showGaps: true, zoomLevel: 16, gapThreshold: GAP_THRESHOLD_MS },
            getLat,
            getLng,
            getTimestamp,
            samplingFrequency: props.samplingFrequency,
            legBridgeMs: GAP_THRESHOLD_MS,
            cut,
            selectedRanges: matched,
            resolveRangeColor: (r: any) =>
              typeof r.color === 'string' && r.color.trim() !== '' ? r.color : group.color,
            simplify: {
              importance: importanceFor(group.sourceId, fullSorted),
              tolerance: trackSimplifyTolerance(currentZoom),
            },
          });
          selFeatures.push(...sel.selection.features);
        }
      }
    }

    map.getSource(baseSrc)?.setData({ type: 'FeatureCollection', features: baseFeatures });
    map.getSource(selSrc)?.setData({ type: 'FeatureCollection', features: selFeatures });
  };

  // Rebuild on map 'idle' too (after load + after any repaint settles) so late-loading sources are drawn and
  // the fleet never stays stale — same reliability the Canvas overlays get from the map render cycle.
  const onIdle = () => rebuild();

  createEffect(() => {
    const map = props.map;
    if (!map) return;
    if (!styleReady()) {
      map.once('styledata', () => {
        ensureLayers(map);
        map.on('idle', onIdle);
        rebuild();
      });
      return;
    }
    ensureLayers(map);
    map.on('idle', onIdle);
  });

  // Rebuild synchronously in the same reactive flush as the boat/maneuver effects (updates together on
  // click/scrub). Rebuild is guarded by try/catch.
  createEffect(() => {
    void props.data;
    void props.selectedSourceIds;
    void props.highlightedSourceIds;
    void props.maptype;
    void props.effectivePlaybackTime;
    void selectedTime();
    void timeWindow();
    void selectedRange();
    void selectedRanges();
    void cutEvents();
    void isCut();
    untrack(() => rebuild());
  });

  onCleanup(() => {
    const map = props.map;
    if (map) map.off('idle', onIdle);
    if (map) removeLayers(map);
  });

  return null;
}
